import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ReactMarkdown from 'react-markdown';
import './MessageFeedbackAnalytics.css';

const API_BASE_URL = '/api';

const MessageFeedbackAnalytics = () => {
  // State
  const [stats, setStats] = useState({
    totalLikes: 0,
    totalDislikes: 0,
    likeRatio: 0,
    dislikeRatio: 0,
    avgLikesPerMessage: 0
  });
  const [trendData, setTrendData] = useState([]);
  const [topLiked, setTopLiked] = useState([]);
  const [topDisliked, setTopDisliked] = useState([]);
  const [books, setBooks] = useState([]);
  const [exams, setExams] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    bookId: '',
    examId: ''
  });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Fetch all data on component mount and when filters change
  useEffect(() => {
    fetchBooks();
    fetchExams();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [filters]);

  // Fetch books for filter
  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/message-feedback/books`, {
        withCredentials: true
      });
      if (response.data.success) {
        setBooks(response.data.books);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  // Fetch exams for filter
  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/message-feedback/exams`, {
        withCredentials: true
      });
      if (response.data.success) {
        setExams(response.data.exams);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  // Fetch all analytics data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.bookId) params.bookId = filters.bookId;
      if (filters.examId) params.examId = filters.examId;

      // Fetch all data in parallel
      const [statsRes, trendRes, topLikedRes, topDislikedRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/message-feedback/overview-stats`, {
          params,
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/admin/message-feedback/trend-data`, {
          params,
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/admin/message-feedback/top-liked`, {
          params,
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/admin/message-feedback/top-disliked`, {
          params,
          withCredentials: true
        })
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }
      if (trendRes.data.success) {
        setTrendData(trendRes.data.trendData);
      }
      if (topLikedRes.data.success) {
        setTopLiked(topLikedRes.data.topLiked);
      }
      if (topDislikedRes.data.success) {
        setTopDisliked(topDislikedRes.data.topDisliked);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      showNotification('Failed to load analytics data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // View message details
  const viewMessageDetails = async (chatId, messageIndex) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/message-feedback/message/${chatId}/${messageIndex}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setSelectedMessage(response.data.message);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching message details:', error);
      showNotification('Failed to load message details', 'error');
    }
  };

  // Close modal
  const closeModal = () => {
    setModalOpen(false);
    setSelectedMessage(null);
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      bookId: '',
      examId: ''
    });
  };

  // Prepare pie chart data
  const pieData = [
    { name: 'Likes', value: stats.totalLikes, color: '#28a745' },
    { name: 'Dislikes', value: stats.totalDislikes, color: '#dc3545' }
  ];

  return (
    <div className="message-feedback-analytics-container">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="analytics-header">
        <div className="header-left">
          <h1 className="page-title">Message Feedback Analytics</h1>
          <p className="page-description">
            Comprehensive analytics for chat message likes and dislikes
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <input
            type="date"
            className="date-input"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            placeholder="From Date"
          />
          <input
            type="date"
            className="date-input"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            placeholder="To Date"
          />
          <select
            className="filter-select"
            value={filters.bookId}
            onChange={(e) => handleFilterChange('bookId', e.target.value)}
          >
            <option value="">All Books</option>
            {books.map(book => (
              <option key={book.id} value={book.id}>{book.title}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={filters.examId}
            onChange={(e) => handleFilterChange('examId', e.target.value)}
          >
            <option value="">All Exams</option>
            {exams.map(exam => (
              <option key={exam.id} value={exam.id}>{exam.name}</option>
            ))}
          </select>
          <button className="clear-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading analytics...</div>
      ) : (
        <>
          {/* Overview Statistics */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Total Likes</div>
              <div className="stat-value">{stats.totalLikes} 👍</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Dislikes</div>
              <div className="stat-value">{stats.totalDislikes} 👎</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Like Ratio</div>
              <div className="stat-value">
                {(() => {
                  const likes = Math.floor(stats.totalLikes || 0);
                  const dislikes = Math.floor(stats.totalDislikes || 0);

                  if (likes === 0 && dislikes === 0) {
                    return '0 : 0';
                  }
                  if (dislikes === 0) {
                    return `${likes} : 0`;
                  }
                  if (likes === 0) {
                    return `0 : ${dislikes}`;
                  }

                  // Find GCD to simplify ratio using iterative approach
                  const gcd = (a, b) => {
                    a = Math.abs(a);
                    b = Math.abs(b);
                    while (b !== 0) {
                      const temp = b;
                      b = a % b;
                      a = temp;
                    }
                    return a;
                  };

                  const divisor = gcd(likes, dislikes);
                  return `${likes / divisor} : ${dislikes / divisor}`;
                })()}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg Likes per Message</div>
              <div className="stat-value">{stats.avgLikesPerMessage} 👍</div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-section">
            {/* Trend Line Chart */}
            <div className="chart-card">
              <h3 className="section-title">
                Trend Over Time {filters.dateFrom ? `(Custom Range)` : `(Last 30 Days)`}
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="likes" stroke="#28a745" strokeWidth={2} name="Likes" />
                  <Line type="monotone" dataKey="dislikes" stroke="#dc3545" strokeWidth={2} name="Dislikes" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart */}
            <div className="chart-card">
              <h3 className="section-title">Like/Dislike Ratio</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top 10 Most Liked Messages */}
          <div className="table-section">
            <h3 className="section-title">Top 10 Most Liked Messages</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Message Preview</th>
                    <th>User Email</th>
                    <th>Likes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topLiked.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        No liked messages found
                      </td>
                    </tr>
                  ) : (
                    topLiked.map((msg, index) => (
                      <tr key={`liked-${msg.chat_id}-${msg.message_index}-${index}`}>
                        <td>{index + 1}</td>
                        <td className="message-preview">
                          {msg.message_text.substring(0, 100)}
                          {msg.message_text.length > 100 && '...'}
                        </td>
                        <td className="email-cell">{msg.user_email}</td>
                        <td className="like-count">{msg.like_count} 👍</td>
                        <td>
                          <button
                            className="view-btn"
                            onClick={() => viewMessageDetails(msg.chat_id, msg.message_index)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top 10 Most Disliked Messages */}
          <div className="table-section">
            <h3 className="section-title">Top 10 Most Disliked Messages</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Message Preview</th>
                    <th>User Email</th>
                    <th>Dislikes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topDisliked.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        No disliked messages found
                      </td>
                    </tr>
                  ) : (
                    topDisliked.map((msg, index) => (
                      <tr key={`disliked-${msg.chat_id}-${msg.message_index}-${index}`}>
                        <td>{index + 1}</td>
                        <td className="message-preview">
                          {msg.message_text.substring(0, 100)}
                          {msg.message_text.length > 100 && '...'}
                        </td>
                        <td className="email-cell">{msg.user_email}</td>
                        <td className="dislike-count">{msg.dislike_count} 👎</td>
                        <td>
                          <button
                            className="view-btn"
                            onClick={() => viewMessageDetails(msg.chat_id, msg.message_index)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Message Detail Modal */}
      {modalOpen && selectedMessage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Message Details</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {/* Feedback Stats */}
              <div className="detail-section">
                <h3 className="section-title">Feedback Stats</h3>
                <div className="feedback-stats">
                  <span className="stat-item">Likes: {selectedMessage.feedback.likes} 👍</span>
                  <span className="stat-separator">|</span>
                  <span className="stat-item">Dislikes: {selectedMessage.feedback.dislikes} 👎</span>
                  <span className="stat-separator">|</span>
                  <span className="stat-item">
                    Ratio: {selectedMessage.feedback.likeRatio}% positive
                  </span>
                </div>
              </div>

              {/* Metadata */}
              <div className="detail-section">
                <h3 className="section-title">Metadata</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Date</label>
                    <span>{new Date(selectedMessage.created_at).toLocaleString()}</span>
                  </div>
                  <div className="info-item">
                    <label>User Email</label>
                    <span>{selectedMessage.user_email}</span>
                  </div>
                </div>
              </div>

              {/* Full Message */}
              <div className="detail-section">
                <h3 className="section-title">Full Message Text</h3>
                <div className="message-box">
                  {(() => {
                    try {
                      const parsed = JSON.parse(selectedMessage.content);
                      if (Array.isArray(parsed)) {
                        return parsed.map((item, idx) => (
                          <div key={idx} style={{ marginBottom: '12px' }}>
                            {item.type === 'text' && (
                              <ReactMarkdown>{item.text}</ReactMarkdown>
                            )}
                            {item.type === 'image_url' && (
                              <div>
                                <img
                                  src={item.image_url?.url}
                                  alt="Message"
                                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                                />
                              </div>
                            )}
                          </div>
                        ));
                      }
                      return <ReactMarkdown>{selectedMessage.content}</ReactMarkdown>;
                    } catch {
                      return <ReactMarkdown>{selectedMessage.content}</ReactMarkdown>;
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageFeedbackAnalytics;
